/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Feature Cards Section - 功能卡片展示区块
 */
import { ArrowRightOutlined } from '@ant-design/icons';
import Link from 'next/link';
import React from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';

import styles from './index.module.scss';
import VideoCard from './VideoCard';

export interface VideoItem {
  /** 视频唯一 ID */
  id: string;
  /** 视频 URL */
  videoUrl: string;
  /** 封面图 URL */
  posterUrl: string;
  /** 视频 alt 文本 */
  alt: string;
}

export interface FeatureCardItem {
  /** 卡片唯一 ID */
  id: string;
  /** 卡片标题 */
  title: string;
  /** 卡片描述 */
  description: string;
  /** 跳转链接 */
  href: string;
  /** 视频列表 */
  videos: VideoItem[];
}

export interface FeatureCardsSectionProps {
  /** 区块标题 */
  title: string;
  /** 高亮标题（第二行） */
  highlightTitle?: string;
  /** 区块副标题 */
  subtitle: string;
  /** 功能卡片列表 */
  cards: FeatureCardItem[];
  /** CTA 按钮文本 */
  ctaText: string;
  /** CTA 跳转链接 */
  ctaUrl?: string;
}

const FeatureCardsSection: React.FC<FeatureCardsSectionProps> = ({
  title,
  highlightTitle,
  subtitle,
  cards,
  ctaText,
  ctaUrl = '/ai-video-generator',
}) => {
  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题区域 */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            {title}
            {highlightTitle && (
              <>
                <br />
                <span className={styles.highlightTitle}>{highlightTitle}</span>
              </>
            )}
          </h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {/* 卡片网格 */}
        <div className={styles.cardsGrid}>
          {cards.map((card) => (
            <div key={card.id} className={styles.card}>
              {/* 卡片头部 */}
              <div className={styles.cardHeader}>
                <h3 className={styles.cardTitle}>{card.title}</h3>
                <Link href={card.href} className={styles.cardLink}>
                  <ArrowRightOutlined />
                </Link>
              </div>

              {/* 卡片描述 */}
              <p className={styles.cardDescription}>{card.description}</p>

              {/* 视频列表 */}
              <div className={styles.videosGrid}>
                {card.videos.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    videoUrl={video.videoUrl}
                    posterUrl={video.posterUrl}
                    alt={video.alt}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* CTA 按钮 */}
        <div className={styles.ctaWrapper}>
          <StartButton text={ctaText} href={ctaUrl} className={styles.ctaButton} />
        </div>
      </div>
    </section>
  );
};

export default FeatureCardsSection;
